import React, { useState, useEffect } from "react";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import { Typography, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ExternalConnectionsTableDropdown from "./tempComponents/externalConnections/tableDropdown";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid.js";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ManageConnectionHeader from "../../../components/generic/arpariBoxes/ManageConnectionHeader.js";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearWithValueLabel from "../../../components/generic/linearProgressBar";
import HideOnMobile from "../../../components/generic/hideOnMobile";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox.js";

const SOURCES = ['FI', 'PL', 'FT', 'CX']

const ManageConnectionsDetails = () => {

    // all source groups
    const [sourceGroups, setSourceGroups] = useState([]);
    const [sourceGroupedByInstitution, setSourceGroupedByInstitution] = useState([]);
    const [loadingSourceGroups, setLoadingSourceGroups] = useState(true);
    const [countExpected, setCountExpected] = useState(Object.fromEntries(SOURCES.map((source) => [source, -1])));
    const [countReceived, setCountReceived] = useState(Object.fromEntries(SOURCES.map((source) => [source, 0])));

    const plaidAccessTokenOnSuccess = (plaidAccountsResponse, type) => {
        const sourceGroupId = plaidAccountsResponse?.data?.source_group_id;
        if (!sourceGroupId) return;
        backendGetRequest(
            "/api/external-connections/source-group/",
            { params: { source: "PL", sourceGroupId: sourceGroupId } },
            ""
        ).then((res) => {
            setSourceGroups(prevState => [...prevState, res?.data?.data]);
        });
    };


    useEffect(() => {
        const isExpectedInit = SOURCES.every(source => countExpected[source] !== -1);
        if (!isExpectedInit) return;
        const isLoading = SOURCES.some(source => countExpected[source] > countReceived[source]);
        setLoadingSourceGroups(isLoading);
    }, [sourceGroups, countReceived, countExpected]);


    const columns = [
        {
            field: 'institution_name',
            headerName: 'Institution',
            flex: 2,
            sortable: true,
            renderCell: (params) => (
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                }}>
                    <Typography>
                        {params?.row?.institution_name}
                    </Typography>
                    {params?.row?.institution_id && (
                        <Link
                            to={`/manage-connections/details/${params?.row?.source}-${params?.row?.institution_id?.slice(2)}`}
                            color="info" underline="hover" variant="subtitle2" style={{ paddingLeft: "8px", paddingTop: "1px" }}>
                            View Account Connections <EastOutlinedIcon />
                        </Link>
                    )}
                </Box>
            ),
        },
        {
            field: 'connection_count',
            headerName: 'No. Connections',
            flex: 0.75,
            renderCell: (params) => (
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Typography> {params?.row?.connection_count} </Typography>
                    {params?.row?.requires_attention && <ErrorOutlineOutlinedIcon color="warning" sx={{ marginLeft: "10px" }} />}
                </Box>
            ),
        },
        {
            field: 'source_type_description',
            headerName: 'Source Type',
            flex: 0.75,
            renderCell: (params) => (
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Typography>
                        {params?.row?.source_type_description}
                    </Typography>
                </Box>
            ),
        }
    ];

    useEffect(() => {
        getAllExternalAccounts();
    }, []);

    const getAllExternalAccounts = () => {
        setSourceGroups([]);
        for (const source of SOURCES) {
            getSourceGroups(source);
            getSourceGroupsRefresh(source)
        }
    }

    const getSourceGroupsRefresh = async (sourceTag) => {
        const sourceGroupRefreshURL = "/api/external-connections/refresh-v2/";
        let params = {
            pageIndex: 0,
            source: sourceTag,
        };
        try {
            const response = await backendGetRequest(sourceGroupRefreshURL, { params: params }, "");
            let totalPages = response?.data?.total_pages || 0
            for (let i = 1; i <= totalPages; i++) {
                let params = { pageIndex: i, source: sourceTag };
                try {
                    backendGetRequest(
                        sourceGroupRefreshURL,
                        { params: params },
                    ).then((res) => {
                        if (res?.data) {
                            const newSourceGroups = res?.data?.data || [];
                            setSourceGroups(prevState => {
                                const existingMap = new Map(prevState.map(item => [item.id, item]));
                                newSourceGroups.forEach(newItem => {
                                    existingMap.set(newItem.id, newItem);
                                });
                                return Array.from(existingMap.values());
                            });
                        }
                    });
                } catch (error) {
                    console.error(`Error fetching page ${i}:`, error);
                }
            }
        } catch (error) {
            console.error('Error fetching external connections:', error);
        }
    }

    useEffect(() => {
        const combinedGroups = sourceGroups.reduce((acc, group) => {
            const existingInstitution = acc.find(item => {
                if (item?.institution_id) return item.institution_id === group.institution_id;
                return item.id === group.id;
            });
            if (existingInstitution) {
                existingInstitution.institution_id = group.institution_id;
                existingInstitution.live_count += group.live_count;
                existingInstitution.assigned_count += group.assigned_count;
                existingInstitution.connection_count += group.connection_count;
                existingInstitution.requires_attention = existingInstitution.requires_attention || group.requires_attention;
            } else {
                acc.push({
                    id: group.id,
                    source: group.source,
                    institution_id: group.institution_id,
                    source_group_id: group.source_group_id,
                    institution_name: group.institution_name,
                    live_count: group.live_count,
                    assigned_count: group.assigned_count,
                    connection_count: group.connection_count,
                    requires_attention: group.requires_attention,
                    source_type_description: group.source_type_description,
                    source_groups: [group],
                });
            }
            return acc;
        }, []);
        setSourceGroupedByInstitution(prevState => combinedGroups);
    }, [sourceGroups]);

    const getSourceGroups = async (sourceTag) => {
        const sourceGroupURL = "/api/external-connections/source-group/";
        let params = {
            pageIndex: 0,
            source: sourceTag,
        };
        try {
            const response = await backendGetRequest(sourceGroupURL, { params: params }, "");
            let totalPages = response?.data?.total_pages || 0;
            if (response?.data?.total_records === 0) {
                totalPages = 0
            }
            setCountExpected(prevState => ({
                ...prevState,
                [sourceTag]: totalPages,
            }));
            for (let i = 1; i <= totalPages; i++) {
                let params = { pageIndex: i, source: sourceTag };
                try {
                    backendGetRequestWithCache(
                        sourceGroupURL,
                        { params: params },
                        (cachedResponse) => {
                            if (cachedResponse?.data) {
                                const newSourceGroups = cachedResponse?.data?.data || [];
                                setSourceGroups(prevState => {
                                    const existingIds = new Set(prevState.map(item => item.id));
                                    const updatedGroups = [...prevState];
                                    newSourceGroups.forEach(newItem => {
                                        if (!existingIds.has(newItem.id)) {
                                            updatedGroups.push(newItem);
                                        }
                                    });
                                    return updatedGroups;
                                });
                            }
                        },
                        (nonCachedResponse) => {
                            if (nonCachedResponse?.data) {
                                const newSourceGroups = nonCachedResponse?.data?.data || [];
                                setSourceGroups(prevState => {
                                    const existingMap = new Map(prevState.map(item => [item.id, item]));
                                    newSourceGroups.forEach(newItem => {
                                        existingMap.set(newItem.id, newItem);
                                    });
                                    return Array.from(existingMap.values());
                                });
                                setCountReceived(prevState => ({
                                    ...prevState,
                                    [sourceTag]: prevState[sourceTag] + 1,
                                }));
                            }
                        },
                        (error) => { // error handling
                            setCountExpected(prevState => ({
                                ...prevState,
                                [sourceTag]: prevState[sourceTag] - 1,
                            }));
                        }
                    );
                } catch (error) {
                    console.error(`Error fetching page ${i}:`, error);
                }
            }
        } catch (error) {
            console.error('Error fetching external connections:', error);
        }
    };

    return (

        <OuterBox>
            {sourceGroupedByInstitution && sourceGroupedByInstitution.filter(item => item.requires_attention).length ? <Alert severity="info" onClose={() => { }}>
                {sourceGroupedByInstitution.filter(item => item.requires_attention).length} / {sourceGroupedByInstitution.length} require your attention.
                Connections at these banks may require reconnection or the account has been closed:{" "}
                {sourceGroupedByInstitution
                    .filter(item => item.requires_attention)
                    .map(item => item.institution_name)
                    .join(", ")}
                .
            </Alert> : null}
            <ManageConnectionHeader
                title="Manage Connections"
                totalHealthy={sourceGroupedByInstitution.filter(item => !item.requires_attention).length}
                total={sourceGroupedByInstitution.length}
                connectionHealthSuffix={" institutions"}
                titleBoxExtra={
                    <Accordion
                        disableGutters
                        defaultExpanded
                        elevation={0}
                        sx={{
                            '&:before': {
                                display: 'none',
                            }
                        }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ margin: 0 }} />} sx={{ padding: 0, mb: -1 }}>
                            <Typography variant="overline" fontSize={11}>Breakdown</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ maxHeight: "100px", overflowY: "auto", padding: 0 }}>
                            <Stack direction="column" spacing={-1}>
                                {Object.entries({
                                    'FT': "File Transfer",
                                    'FI': "Finicity",
                                    'PL': "Plaid",
                                    'CX': 'Custom API',
                                }).map(([source, sourceDescription]) => {
                                    var healthyCount = sourceGroupedByInstitution.filter(item => item.source === source && !item.requires_attention).length
                                    var sourceTotal = sourceGroupedByInstitution.filter(item => item.source === source).length
                                    if (!sourceTotal) return undefined;
                                    return <>
                                        <Typography fontSize={10} sx={{ color: 'text.secondary' }}>{`${sourceDescription} - ${healthyCount} / ${sourceTotal} institutions`}</Typography>
                                        <LinearWithValueLabel
                                            numerator={healthyCount}
                                            denominator={sourceTotal}
                                            show100={true}
                                        />
                                    </>
                                }
                                )}

                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                }
                actions={
                    <HideOnMobile>
                        <ExternalConnectionsTableDropdown
                            plaidAccessTokenOnSuccess={plaidAccessTokenOnSuccess}
                            refreshAccounts={getAllExternalAccounts}
                            selectedRows={[]}
                            tableData={sourceGroupedByInstitution}
                        />
                    </HideOnMobile>}
                stat1={sourceGroupedByInstitution.reduce((acc, item) => acc + (item.connection_count || 0), 0)}
                stat1Title={"Connections"}
                stat2={sourceGroupedByInstitution.length}
                stat2Title={"Institutions"}
            />
            <TableWithLoadingWrapper
                columns={columns}
                pagination={{
                    paginationModel: { pageSize: sourceGroups?.length || 50, page: 0 },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                dataSource={sourceGroupedByInstitution}
                getrowId={row => row.institution_id}
                numLoadingEntries={3}
                sortModel={[
                    {
                        field: "institution_name",
                        sort: "asc",
                    },
                ]}
                loading={loadingSourceGroups}
                loadingEntriesTop={true}
            />
        </OuterBox>

    );
}

export default ManageConnectionsDetails;
